import { FC, useContext, useEffect, useRef, useState } from "react";
import NotificationDispatch, { showErrorNotification } from "../../../../../context/notificationContext";
import { useSearchContext } from "../../../../../context/searchContext";
import { useDebounce } from "../../../../../hooks/useDebounce";
import { useEntityAccesses } from "../../../../../hooks/useEntityAccesses";
import { useFilterSearch } from "../../../../../hooks/useFilterSearch";
import useListFilters from "../../../../../hooks/useListFilters";
import useI18n from "../../../../../hooks/useTranslations";
import { HrailSupport } from "../../../../../http/hrail/types/supports";
import { useHrailSupportsApi } from "../../../../../http/hrail/useSupports";
import { PaginationQuery } from "../../../../../types";
import FlexContent from "../../../../ui/FlexContent";
import InlineOrderEditor from "../../../../ui/InlineOrderEditor";
import { getDeleteAction, getEditAction } from "../../../../ui/ListItemMenu";
import ModalDialog from "../../../../ui/ModalDialog";
import SearchBar from "../../../../ui/SearchBar";
import Table from "../../../../ui/Table";
import Visibility from "../../../../ui/Visibility";
import { VisibilityScopeValue } from "../../../../ui/VisibilityScope";
import CreateSupportModalDialogButton from "./CreateSupportModalDialogButton";
import SupportForm from "./SupportForm";

type Props = {
  active?: boolean;
};

const Supports: FC<Props> = ({ active }) => {
  const i18n = useI18n();
  const dispatch = useContext(NotificationDispatch);
  const filters = useListFilters({ key: "supports" });
  const aborter = useRef(new AbortController());
  const supportApi = useHrailSupportsApi();

  const [itemToEdit, setItemToEdit] = useState<HrailSupport>();
  const [itemToDelete, setItemToDelete] = useState<HrailSupport>();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { setActions, setTitle } = useSearchContext();
  const { searchTerm, handleSearchChange } = useFilterSearch(filters, aborter, "supports");
  const isActiveTab = active ?? true;
  const updateVisibility = async (support: HrailSupport, visibility: VisibilityScopeValue) => {
    try {
      await supportApi.updateAdminHrailSupportVisibility(support.id, { visibility });
    } catch (err) {
      dispatch(showErrorNotification(i18n, err));
    }
  };

  const updateOrder = async (support: HrailSupport, newOrder: number) => {
    try {
      await supportApi.updateAdminHrailSupportOrder(support.id, { order: newOrder });
    } catch (err) {
      dispatch(showErrorNotification(i18n, err));
    }
  };

  const { accessesAction, accessesDialog } = useEntityAccesses<HrailSupport>("hrail.support");

  const debouncedValue = useDebounce(searchTerm);

  const query: PaginationQuery = {
    ...filters.state,
    q: debouncedValue,
  };

  const { error, data } = supportApi.useAdminHrailSupports(query, aborter.current.signal);

  useEffect(() => {
    if (!isActiveTab) return;

    setTitle(i18n.translation.supports.plural);
    setActions(
      <>
        <SearchBar
          placeholder={i18n.translation.supports.search}
          value={searchTerm}
          onChange={handleSearchChange}
        />
        <CreateSupportModalDialogButton />
      </>,
    );

    return () => {
      setTitle(undefined);
      setActions(null);
    };
  }, [isActiveTab, searchTerm]);

  return (
    <FlexContent padding>
      <Table<HrailSupport>
        isTall={true}
        filters={filters}
        head={
          <tr>
            <th align="left">{i18n.translation.common.code}</th>
            <th align="left">{i18n.translation.items.plural}</th>
            <th>{i18n.translation.common.visibility}</th>
            <th>{i18n.translation.common.order}</th>
            <th></th>
          </tr>
        }
        items={data?.items}
        renderRow={(support) => (
          <>
            <Table.Td noWrap={true}>{support.code ?? ""}</Table.Td>
            <Table.Td noWrap={false}>{getSupportItemsLabel(support)}</Table.Td>
            <td align="center">
              <Visibility
                value={support.visibility}
                onChange={(value) => updateVisibility(support, value)}
              />
            </td>
            <td align="center">
              <InlineOrderEditor
                order={support.order}
                onUpdate={(newOrder) => updateOrder(support, newOrder)}
              />
            </td>
          </>
        )}
        onRowClick={(support) => setItemToEdit(support)}
        rowActions={(support) => [
          getEditAction(i18n, () => setItemToEdit(support)),
          accessesAction(support),
          getDeleteAction(i18n, () => setItemToDelete(support), true),
        ]}
        total={data?.total}
        error={!!error}
      />
      {accessesDialog}

      {!!itemToEdit && (
        <SupportEditDialog support={itemToEdit} onClose={() => setItemToEdit(undefined)} />
      )}

      {!!itemToDelete && (
        <ModalDialog
          onClose={() => setItemToDelete(undefined)}
          title={i18n.translation.supports.delete}
          isSubmitting={isSubmitting}
          actions={[
            {
              title: i18n.translation.common.cancel,
              onClick: () => setItemToDelete(undefined),
              type: "secondary",
            },
            {
              title: i18n.translation.common.yes,
              onClick: () => {
                setIsSubmitting(true);
                supportApi
                  .deleteAdminHrailSupport(itemToDelete.id)
                  .then(() => setItemToDelete(undefined))
                  .catch((err) => dispatch(showErrorNotification(i18n, err)))
                  .finally(() => setIsSubmitting(false));
              },
            },
          ]}
        >
          {i18n.translation.common.deleteEntityQuestion(
            i18n.translation.supports.deleteLabel,
            itemToDelete.code || itemToDelete.id,
          )}
        </ModalDialog>
      )}
    </FlexContent>
  );
};

interface SupportEditDialogProps {
  support: HrailSupport;
  onClose: () => void;
}

const SupportEditDialog: FC<SupportEditDialogProps> = ({ support, onClose }) => {
  const i18n = useI18n();
  const supportApi = useHrailSupportsApi();
  const aborter = useRef(new AbortController());
  const { error, data, isMutating, trigger } = supportApi.getAdminHrailSupport(
    support.id,
    aborter.current.signal,
  );

  useEffect(() => {
    trigger();

    return () => aborter.current.abort();
  }, [support.id]);

  return (
    <ModalDialog onClose={onClose} title={i18n.translation.supports.edit}>
      <SupportForm
        support={!error ? data : undefined}
        isLoading={!data}
        isDisabled={isMutating || !!error}
        onClose={onClose}
        onSaved={() => trigger()}
      />
    </ModalDialog>
  );
};

const getSupportItemsLabel = (support: HrailSupport) =>
  support.items
    ?.map((row) => row.item?.code)
    .filter(Boolean)
    .join(" + ") ||
  support.code ||
  "-";

export default Supports;
